import { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/layout/Sidebar';
import HostingNav from '../components/layout/HostingNav';
import PageHeader from '../components/PageHeader';
import { Badge, Button, Card, SegmentedControl, SidePanel, Table, Tabs, useToast } from '../components/ui';
import type { BadgeTone, TableColumn } from '../components/ui';
import { useUser } from '../context/UserContext';
import { useUrlParam } from '../hooks/useUrlParam';
import { timeAgo } from '../lib/comms';
import AuditsView from '../components/security/AuditsView';
import {
  DECISION_LABEL,
  allowedDecisions,
  decisionEffect,
  fetchInventory,
  lastActivity,
  liveUrl,
  setDecisionNote,
  setDecisions,
  type InventoryDecision,
  type InventoryRow,
  type InventoryVerdict,
} from '../lib/infraInventory';

type TabValue = InventoryVerdict | 'decided' | 'done';

const TABS: Array<{ value: TabValue; label: string }> = [
  { value: 'junk', label: 'Junk' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'keep', label: 'Keep' },
  { value: 'decided', label: 'Waiting to run' },
  { value: 'done', label: 'Done' },
];

const VERDICT_TONE: Record<InventoryVerdict, BadgeTone> = { keep: 'up', unsure: 'warn', junk: 'down' };
const DECISION_TONE: Record<InventoryDecision, BadgeTone> = { keep: 'up', archive: 'info', delete: 'down' };
const UNDECIDED = 'none';

const isDone = (r: InventoryRow) => Boolean(r.executed_at) || (r.decision === 'keep' && Boolean(r.decided_at));
const isWaiting = (r: InventoryRow) => (r.decision === 'archive' || r.decision === 'delete') && !r.executed_at;

function inTab(r: InventoryRow, tab: TabValue): boolean {
  if (tab === 'decided') return isWaiting(r);
  if (tab === 'done') return isDone(r);
  return r.verdict === tab && !r.decision;
}

const faint: React.CSSProperties = { color: 'var(--text-faint)' };


/**
 * Security: two views, in the URL as ?view=. Inventory is what exists today (every repo and Netlify site, sorted for
 * cleanup); Audits is every system (a database and what uses it, or a repo with no database) with its Audit button.
 */
export default function Security() {
  const [viewParam] = useUrlParam('view');
  const view = viewParam === 'audits' ? 'audits' : 'inventory';
  const [search, setSearch] = useState('');
  const [count, setCount] = useState<number | undefined>(undefined);
  // The submenu switches views by link; drop the other view's count until this one reports.
  useEffect(() => setCount(undefined), [view]);

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Security"
          count={count}
          searchPlaceholder={view === 'audits' ? 'Search systems, repos, sites…' : 'Search repos, sites, domains…'}
          searchValue={search}
          onSearchChange={setSearch}
        />
        <HostingNav />
        {view === 'audits' ? <AuditsView search={search} onCount={setCount} /> : <InventoryView search={search} onCount={setCount} />}
      </main>
    </div>
  );
}

/**
 * Security → Inventory: every repo and Netlify site, sorted keep / junk / unsure
 * by scripts/infra-inventory.mjs. A click here records the decision only; the
 * script's execute step does the archiving and deleting, so no GitHub or Netlify
 * key with delete rights ever sits in the app.
 */
function InventoryView({ search, onCount }: { search: string; onCount: (n: number) => void }) {
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabValue>('junk');
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState<InventoryDecision | null>(null);
  const [note, setNote] = useState('');
  const [openId, setOpenId] = useUrlParam('item');
  const { user } = useUser();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setRows(await fetchInventory());
    } catch (err) {
      console.error('Error loading inventory:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load the inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loading) onCount(rows.length);
  }, [rows.length, loading, onCount]);

  // The panel is the URL: ?item=<id> opens it, so refresh and back work.
  const open = rows.find((r) => r.id === openId) ?? null;

  useEffect(() => {
    setNote(open?.decision_note ?? '');
  }, [open?.id, open?.decision_note]);

  useEffect(() => {
    setConfirmBulk(null);
  }, [tab, search]);

  const counts = useMemo(() => {
    const c = {} as Record<TabValue, number>;
    for (const t of TABS) c[t.value] = rows.filter((r) => inTab(r, t.value)).length;
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => inTab(r, tab))
      .filter((r) => !q || [r.name, r.client_name, r.verdict_reason, ...(r.facts.domains ?? []).map((d) => d.domain)].some((v) => v?.toLowerCase().includes(q)));
  }, [rows, tab, search]);

  const collectedAt = useMemo(() => rows.reduce<string | null>((a, r) => (!a || r.seen_at > a ? r.seen_at : a), null), [rows]);

  const decide = useCallback(
    async (ids: string[], decision: InventoryDecision | null) => {
      if (!ids.length) return;
      setSavingIds((s) => new Set([...s, ...ids]));
      try {
        await setDecisions(ids, decision, user?.id ?? null);
        const at = new Date().toISOString();
        setRows((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, decision, decided_at: decision ? at : null } : r)));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not save the decision');
      } finally {
        setSavingIds((s) => new Set([...s].filter((id) => !ids.includes(id))));
      }
    },
    [user?.id, toast]
  );

  const saveNote = async () => {
    if (!open) return;
    const value = note.trim() || null;
    if (value === (open.decision_note ?? null)) return;
    try {
      await setDecisionNote(open.id, value);
      setRows((prev) => prev.map((r) => (r.id === open.id ? { ...r, decision_note: value } : r)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the note');
    }
  };

  const decisionControl = (r: InventoryRow) => {
    if (r.executed_at) {
      return <Badge tone={DECISION_TONE[r.decision ?? 'keep']}>{r.decision === 'delete' ? 'Deleted' : r.decision === 'archive' ? 'Archived' : 'Done'}</Badge>;
    }
    const options = [{ id: UNDECIDED, label: '—' }, ...allowedDecisions(r).map((d) => ({ id: d, label: DECISION_LABEL[d] }))];
    return (
      <div onClick={(e) => e.stopPropagation()} style={{ opacity: savingIds.has(r.id) ? 0.5 : 1, display: 'inline-flex' }}>
        <SegmentedControl
          options={options}
          value={r.decision ?? UNDECIDED}
          onChange={(id) => decide([r.id], id === UNDECIDED ? null : (id as InventoryDecision))}
        />
      </div>
    );
  };

  const columns: TableColumn<InventoryRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Project',
        width: '26%',
        render: (_, r) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
            <span style={{ fontWeight: 500, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
            <LiveLink row={r} />
            <span style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              <Badge tone="neutral">{r.kind === 'repo' ? 'GitHub' : 'Netlify'}</Badge>
              {r.kind === 'repo' && r.facts.private === false ? <Badge tone="warn">Public</Badge> : null}
              {r.facts.archived ? <Badge tone="idle">Archived</Badge> : null}
              {r.facts.owned === false ? <Badge tone="idle">{`Owner ${r.facts.owner}`}</Badge> : null}
              {r.client_name ? <Badge tone="accent">{r.client_name}</Badge> : null}
            </span>
          </div>
        ),
      },
      {
        key: 'verdict_reason',
        label: 'Why',
        wrap: true,
        render: (_, r) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 'var(--text-sm)' }}>
            <span style={{ color: 'var(--text-muted)' }}>{r.verdict_reason}</span>
            {r.execution_result ? (
              <span style={{ color: r.executed_at ? 'var(--status-up)' : 'var(--status-down)', fontSize: 'var(--text-xs)' }}>{r.execution_result}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'verdict',
        label: 'Sorted as',
        width: '9%',
        render: (_, r) => (
          <Badge tone={VERDICT_TONE[r.verdict]} dot>
            {r.verdict[0].toUpperCase() + r.verdict.slice(1)}
          </Badge>
        ),
      },
      {
        key: 'activity',
        label: 'Last activity',
        width: '10%',
        render: (_, r) => {
          const at = lastActivity(r);
          return at ? <span className="t-num">{timeAgo(at)}</span> : <span style={faint}>—</span>;
        },
      },
      {
        key: 'decision',
        label: 'Your call',
        width: '290px',
        align: 'right',
        render: (_, r) => decisionControl(r),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savingIds, decide]
  );

  // Bulk: only rows that can take the decision, and only undecided ones.
  const bulkTargets = (d: InventoryDecision) => visible.filter((r) => !r.decision && !r.executed_at && allowedDecisions(r).includes(d)).map((r) => r.id);
  const bulkBar =
    tab === 'junk' || tab === 'unsure' || tab === 'keep' ? (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {(tab === 'keep' ? (['keep'] as InventoryDecision[]) : (['delete', 'keep'] as InventoryDecision[])).map((d) => {
          const n = bulkTargets(d).length;
          if (!n) return null;
          const confirming = confirmBulk === d;
          return confirming ? (
            <span key={d} style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{`Mark ${n} shown as ${DECISION_LABEL[d]}?`}</span>
              <Button size="sm" variant={d === 'delete' ? 'danger' : 'primary'} onClick={() => { setConfirmBulk(null); decide(bulkTargets(d), d); }}>
                Yes
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmBulk(null)}>
                Cancel
              </Button>
            </span>
          ) : (
            <Button key={d} size="sm" variant={d === 'delete' ? 'secondary' : 'ghost'} onClick={() => setConfirmBulk(d)}>
              {`Mark all ${n} ${DECISION_LABEL[d]}`}
            </Button>
          );
        })}
      </div>
    ) : null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <Tabs
          tabs={TABS.map((t) => `${t.label} ${counts[t.value] ?? 0}`)}
          active={`${TABS.find((t) => t.value === tab)?.label} ${counts[tab] ?? 0}`}
          onChange={(label) => {
            const def = TABS.find((t) => label.startsWith(`${t.label} `));
            if (def) setTab(def.value);
          }}
        />
        {bulkBar}
      </div>

      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: '0 0 var(--space-5)' }}>
        {tab === 'decided'
          ? 'Decided and not yet carried out. Owlbert runs these with infra-inventory.mjs execute, which refuses anything whose domain still answers.'
          : `Every GitHub repo and Netlify site we can reach${collectedAt ? `, collected ${timeAgo(collectedAt)}` : ''}. Your call is only recorded here; nothing is archived or deleted until the execute step runs.`}
      </p>

      {loadError ? (
        <Card>
          <div style={{ color: 'var(--status-down)', fontSize: 'var(--text-sm)' }}>{loadError}</div>
        </Card>
      ) : loading ? null : visible.length === 0 ? (
        <Card>
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            {rows.length === 0 ? 'No inventory yet. Run scripts/infra-inventory.mjs collect.' : 'Nothing here.'}
          </div>
        </Card>
      ) : (
        <>
          {/* Phones: a card per project. */}
          <div
            className="md:hidden"
            style={{ background: 'var(--surface-card)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}
          >
            {visible.map((r, i) => (
              <div
                key={r.id}
                onClick={() => setOpenId(r.id)}
                style={{ padding: '14px', borderTop: i === 0 ? 'none' : '1px solid var(--border-hairline)', display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-body)', wordBreak: 'break-all' }}>{r.name}</span>
                  <Badge tone={VERDICT_TONE[r.verdict]} dot>
                    {r.verdict}
                  </Badge>
                </div>
                <LiveLink row={r} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{r.verdict_reason}</span>
                {decisionControl(r)}
              </div>
            ))}
          </div>
          <div className="hidden md:block" style={{ overflowX: 'auto' }}>
            <Table columns={columns} rows={visible} onRowClick={(r) => setOpenId(r.id)} style={{ minWidth: '1000px' }} />
          </div>
        </>
      )}

      <SidePanel
        open={Boolean(open)}
        onClose={() => setOpenId(null)}
        eyebrow={open?.kind === 'repo' ? 'GitHub repo' : 'Netlify site'}
        title={open?.name ?? ''}
        width={520}
        footer={
          <Button variant="ghost" onClick={() => setOpenId(null)}>
            Close
          </Button>
        }
      >
        {open ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', fontSize: 'var(--text-sm)' }}>
            <Section title="Sorted as">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Badge tone={VERDICT_TONE[open.verdict]} dot>
                  {open.verdict}
                </Badge>
              </div>
              <p style={{ margin: '6px 0 0', color: 'var(--text-muted)' }}>{open.verdict_reason}</p>
            </Section>

            <Section title="Your call">
              {decisionControl(open)}
              {open.decision ? <p style={{ margin: '8px 0 0', color: 'var(--text-muted)' }}>{decisionEffect(open, open.decision)}</p> : null}
              {open.execution_result ? (
                <p style={{ margin: '6px 0 0', color: open.executed_at ? 'var(--status-up)' : 'var(--status-down)' }}>{open.execution_result}</p>
              ) : null}
              <textarea
                className="ck-textarea"
                rows={2}
                placeholder="Note (optional): why, or what to check first"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onBlur={saveNote}
                style={{ marginTop: '10px', width: '100%' }}
              />
            </Section>

            <Section title="Facts">
              <Facts row={open} />
            </Section>

            {(open.facts.domains ?? []).length ? (
              <Section title="Domains">
                {(open.facts.domains ?? []).map((d) => (
                  <div key={d.domain} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '4px 0' }}>
                    <a href={`https://${d.domain}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-body)' }}>
                      {d.domain}
                    </a>
                    <span className="t-num" style={{ color: d.live ? 'var(--status-up)' : 'var(--status-down)' }}>
                      {d.live ? `answers ${d.status}` : `no answer (${d.status ?? '—'})`}
                      {d.dns ? <span style={faint}>{` · DNS ${d.dns}`}</span> : null}
                    </span>
                  </div>
                ))}
              </Section>
            ) : null}

            {open.kind === 'repo' && (open.facts.sites ?? []).length ? (
              <Section title="Netlify sites built from it">
                {(open.facts.sites ?? []).map((s) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '4px 0' }}>
                    <a href={`https://app.netlify.com/sites/${s.name}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-body)' }}>
                      {s.name}
                    </a>
                    <span className="t-num" style={faint}>
                      {s.published_at ? `deployed ${timeAgo(s.published_at)}` : 'never deployed'}
                    </span>
                  </div>
                ))}
              </Section>
            ) : null}
          </div>
        ) : null}
      </SidePanel>
    </>
  );
}

/** The live site, opened in a new tab without opening the row's panel. */
function LiveLink({ row }: { row: InventoryRow }) {
  const url = liveUrl(row);
  if (!url) return <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>No live site</span>;
  const answering = (row.facts.domains ?? []).some((d) => d.live && url === `https://${d.domain}`);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={answering ? 'Answers right now' : 'Did not answer at the last collect'}
      style={{ fontSize: 'var(--text-xs)', color: answering ? 'var(--status-up)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', width: 'fit-content', maxWidth: '100%' }}
    >
      {url.replace(/^https?:\/\//, '')} ↗
    </a>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: '7px' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Facts({ row }: { row: InventoryRow }) {
  const f = row.facts;
  const items: Array<[string, React.ReactNode]> = [];
  if (row.url)
    items.push([
      row.kind === 'repo' ? 'GitHub' : 'URL',
      <a href={row.kind === 'site' ? `https://app.netlify.com/sites/${row.name}` : row.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-body)' }}>
        {row.kind === 'site' ? 'Open in Netlify' : row.url.replace(/^https:\/\//, '')}
      </a>,
    ]);
  if (row.client_name) items.push(['Client', row.client_name]);
  if (f.owner) items.push(['Owner', f.owner]);
  if (row.kind === 'repo') items.push(['Visibility', f.private ? 'Private' : 'Public']);
  if (f.description) items.push(['Description', f.description]);
  if (f.language) items.push(['Language', f.language]);
  if (f.created_at) items.push(['Created', new Date(f.created_at).toLocaleDateString()]);
  if (f.pushed_at) items.push(['Last push', `${new Date(f.pushed_at).toLocaleDateString()} (${timeAgo(f.pushed_at)})`]);
  if (row.kind === 'site') {
    items.push(['Built from', f.repo ?? 'Nothing: deployed by hand']);
    if (f.published_at) items.push(['Last deploy', `${new Date(f.published_at).toLocaleDateString()} (${timeAgo(f.published_at)})`]);
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '6px 12px' }}>
      {items.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <span style={faint}>{k}</span>
          <span style={{ color: 'var(--text-body)', wordBreak: 'break-word' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}
